import { Tags, Bot, Code, Filter, Briefcase, Sparkles, Workflow } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LayoutPageTabs, TabConfig } from '@/components/layouts/LayoutPageTabs';
import { Work4MeTagList } from './Work4MeTagList';
import { Work4MeAgentList } from './Work4MeAgentList';
import { Work4MeFilterList } from './Work4MeFilterList';
import { Work4MeProcessList } from './Work4MeProcessList';
import { Work4MeSkillList } from './Work4MeSkillList';
import { Work4MeScriptList } from './Work4MeScriptList';
import { Work4MeAgentDetail } from './Work4MeAgentDetail';
import { Work4MeProcessDetail } from './Work4MeProcessDetail';
import { Work4MeSkillDetail } from './Work4MeSkillDetail';
import { Work4MeScriptDetail } from './Work4MeScriptDetail';
import { Work4MeTagDetail } from './Work4MeTagDetail';
import { Work4MeTagGroupDetail } from './Work4MeTagGroupDetail';

const VALID_SECTIONS = ['agents', 'processes', 'skills', 'scripts', 'tags', 'tag-groups', 'filters'] as const;
type Section = typeof VALID_SECTIONS[number];

function sectionToTab(section: string): string {
  if (section === 'tag-groups') return 'tags';
  return section;
}

function isValidSection(s: string): s is Section {
  return VALID_SECTIONS.includes(s as Section);
}

export function Work4Me() {
  const navigate = useNavigate();
  const { section, id } = useParams<{ section?: string; id?: string }>();
  const currentSection = section && isValidSection(section) ? section : 'agents';
  const activeTab = sectionToTab(currentSection);

  useEffect(() => {
    if (!section || !isValidSection(section)) {
      navigate('/work4me/agents', { replace: true });
    }
  }, [section, navigate]);

  function renderTabContent(tabId: string): React.ReactNode {
    if (id && tabId === activeTab) {
      switch (currentSection) {
        case 'agents': return <Work4MeAgentDetail />;
        case 'processes': return <Work4MeProcessDetail />;
        case 'skills': return <Work4MeSkillDetail />;
        case 'scripts': return <Work4MeScriptDetail />;
        case 'tags': return <Work4MeTagDetail />;
        case 'tag-groups': return <Work4MeTagGroupDetail />;
      }
    }

    switch (tabId) {
      case 'agents': return <Work4MeAgentList />;
      case 'processes': return <Work4MeProcessList />;
      case 'skills': return <Work4MeSkillList />;
      case 'scripts': return <Work4MeScriptList />;
      case 'tags': return <Work4MeTagList />;
      case 'filters': return <Work4MeFilterList />;
      default: return null;
    }
  }

  const tabs: TabConfig[] = [
    { id: 'agents', label: 'Agents', icon: Bot, content: renderTabContent('agents') },
    { id: 'processes', label: 'Processes', icon: Briefcase, content: renderTabContent('processes') },
    { id: 'skills', label: 'Skills', icon: Sparkles, content: renderTabContent('skills') },
    { id: 'scripts', label: 'Scripts', icon: Code, content: renderTabContent('scripts') },
    { id: 'tags', label: 'Tags', icon: Tags, content: renderTabContent('tags') },
    { id: 'filters', label: 'Filters', icon: Filter, content: renderTabContent('filters') },
  ];

  return (
    <div data-testid="work4me-centre" className="min-h-full">
      <div className="p-4 pb-2 bg-muted">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Workflow className="h-8 w-8" />
            <h1 className="text-4xl font-thin tracking-wide">Work4Me Centre</h1>
          </div>
          <p className="text-muted-foreground w-full">
            Agents, Processes, Skills, Scripts, Tags, and Filters for your AI workforce.
          </p>
        </div>
      </div>

      <LayoutPageTabs
        tabs={tabs}
        defaultTab={activeTab}
        onTabChange={(tabId) => navigate(`/work4me/${tabId}`)}
      />
    </div>
  );
}
